"use client";

import React from "react";
import { Card, CardContent, Spinner } from "@heroui/react";
import * as XLSX from "xlsx";
import { useTranslation } from "react-i18next";

import { logger } from "@/telemetry";
import { normalizeText, extractSalientTokens } from "@/utils/textMatching";

export interface ExcelViewerProps {
  /** Excel document as ArrayBuffer */
  documentData: ArrayBuffer;

  /** Citation text used to highlight matching rows in the table */
  citationText?: string;

  /** Filename for display purposes */
  filename?: string;
}

interface SheetData {
  name: string;
  headers: string[];
  rows: any[][];
}

/**
 * Find rows that match the citation text using token overlap scoring.
 *
 * For each row, all cell values are concatenated and normalised. Salient tokens
 * from the citation are compared against the row string. Rows with at least 30%
 * token overlap are considered matches.
 *
 * @param sheet - The current sheet data (headers + rows)
 * @param citationText - Citation text to match against
 * @returns Set of row indices that match
 */
function findMatchingRows(sheet: SheetData, citationText: string): Set<number> {
  const matchingIndices = new Set<number>();

  if (!citationText || !sheet.rows.length) {
    return matchingIndices;
  }

  const normalisedCitation = normalizeText(citationText);
  const salientTokens = extractSalientTokens(normalisedCitation);

  if (salientTokens.length === 0) {
    return matchingIndices;
  }

  for (let rowIdx = 0; rowIdx < sheet.rows.length; rowIdx++) {
    const row = sheet.rows[rowIdx];
    // Concatenate all cell values into a single string for matching
    const rowText = row
      .map((cell: any) => (cell !== null && cell !== undefined ? String(cell) : ""))
      .join(" ");
    const normalisedRow = normalizeText(rowText);

    // Score = proportion of salient tokens found in the row
    const matchedCount = salientTokens.filter((token) => normalisedRow.includes(token)).length;
    const score = matchedCount / salientTokens.length;

    if (score >= 0.3) {
      matchingIndices.add(rowIdx);
    }
  }

  return matchingIndices;
}

/**
 * Excel Viewer component using sheetjs (xlsx)
 * Displays Excel files with multi-sheet support and citation row highlighting.
 *
 * When citation text is provided, rows with sufficient token overlap are
 * highlighted and the view auto-scrolls to the first match.
 */
export const ExcelViewer: React.FC<ExcelViewerProps> = ({ documentData, citationText }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [sheets, setSheets] = React.useState<SheetData[]>([]);
  const [activeSheet, setActiveSheet] = React.useState<string>("");

  // Parse Excel data
  React.useEffect(() => {
    if (!documentData) {
      return;
    }

    let isMounted = true;

    const parseExcel = async () => {
      try {
        setLoading(true);
        setError(null);

        // Read Excel file using xlsx
        const workbook = XLSX.read(documentData, { type: "array" });

        if (!isMounted) return;

        if (workbook.SheetNames.length === 0) {
          setError(t("No sheets found in Excel file"));
          setLoading(false);

          return;
        }

        // Parse all sheets
        const parsedSheets: SheetData[] = workbook.SheetNames.map((sheetName) => {
          const worksheet = workbook.Sheets[sheetName];

          // Convert sheet to array of arrays
          const data: any[][] = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
            defval: "",
            blankrows: false,
          });

          // Limit to first 1000 rows for performance
          const limitedData = data.slice(0, 1001);

          if (limitedData.length === 0) {
            return {
              name: sheetName,
              headers: [],
              rows: [],
            };
          }

          // First row as headers
          const headers = limitedData[0].map((h: any) => String(h || ""));

          // Remaining rows as data
          const rows = limitedData.slice(1);

          return {
            name: sheetName,
            headers,
            rows,
          };
        });

        setSheets(parsedSheets);
        setActiveSheet(parsedSheets[0]?.name || "");
        setLoading(false);
      } catch (err) {
        logger.error("❌ Failed to parse Excel", err);
        if (isMounted) {
          setError(t("Failed to parse Excel file"));
          setLoading(false);
        }
      }
    };

    parseExcel();

    return () => {
      isMounted = false;
    };
    // `t` is stable in production; including it causes test workers to
    // infinite-loop when the test mock returns a new `t` on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentData]);

  // Derive currentSheet early so hooks below can depend on it.
  // Falls back to an empty sheet when data has not loaded yet.
  const currentSheet = sheets.find((s) => s.name === activeSheet) || sheets[0];

  // Compute which rows match the citation text (token overlap >= 30%).
  // Returns an empty set when there is no sheet data or no citation.
  const matchingRows = React.useMemo(
    () => (currentSheet ? findMatchingRows(currentSheet, citationText || "") : new Set<number>()),
    [currentSheet, citationText],
  );

  // Ref for the first highlighted row so we can auto-scroll to it
  const firstMatchRef = React.useRef<HTMLTableRowElement | null>(null);

  // Auto-scroll to the first matching row after loading completes
  React.useEffect(() => {
    if (!loading && matchingRows.size > 0 && firstMatchRef.current?.scrollIntoView) {
      firstMatchRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [loading, matchingRows]);

  // Determine the first matching row index for the ref assignment
  let firstMatchIdx = -1;

  if (matchingRows.size > 0) {
    for (const idx of matchingRows) {
      if (firstMatchIdx === -1 || idx < firstMatchIdx) firstMatchIdx = idx;
    }
  }

  if (loading) {
    return (
      <div
        aria-label={t("Loading Excel file...")}
        aria-live="polite"
        className="flex items-center justify-center h-[60vh]"
        role="status"
      >
        <Spinner label={t("Loading Excel file...")} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-[60vh]" role="alert">
        <p className="text-danger-500">{error}</p>
      </div>
    );
  }

  if (sheets.length === 0) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <p className="text-default-500">{t("No data found in Excel file")}</p>
      </div>
    );
  }

  return (
    <div className="w-full h-[75vh] overflow-auto bg-content1 p-8">
      <div className="max-w-full space-y-4">
        {/* Sheet tabs (if multiple sheets) */}
        {sheets.length > 1 && (
          <Card>
            <CardContent className="p-2">
              <div
                aria-label="Excel sheets"
                className="flex border-b border-default-200"
                role="tablist"
              >
                {sheets.map((sheet) => (
                  <button
                    key={sheet.name}
                    aria-selected={activeSheet === sheet.name}
                    className={`px-4 py-2 text-sm font-medium transition-colors ${
                      activeSheet === sheet.name
                        ? "border-b-2 border-primary text-primary"
                        : "text-default-500 hover:text-default-700"
                    }`}
                    role="tab"
                    onClick={() => setActiveSheet(sheet.name)}
                  >
                    {sheet.name}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Single sheet name (if only one sheet) */}
        {sheets.length === 1 && (
          <div className="text-sm font-medium text-default-600 px-2">
            Sheet: {currentSheet.name}
          </div>
        )}

        {/* Table Display */}
        <Card>
          <CardContent className="p-0">
            {currentSheet.rows.length === 0 ? (
              <div className="flex items-center justify-center h-32">
                <p className="text-default-500">{t("No data in this sheet")}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-default-100 sticky top-0 z-10">
                    <tr>
                      {/* Row number column */}
                      <th className="px-3 py-2 text-left text-xs font-semibold text-default-600 border-r border-default-200">
                        #
                      </th>
                      {currentSheet.headers.map((header, idx) => (
                        <th
                          key={idx}
                          className="px-3 py-2 text-left text-xs font-semibold text-default-600 border-r border-default-200 whitespace-nowrap"
                        >
                          {header || `Column ${String.fromCharCode(65 + idx)}`}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {currentSheet.rows.map((row, rowIdx) => {
                      const isMatch = matchingRows.has(rowIdx);

                      return (
                        <tr
                          key={rowIdx}
                          ref={rowIdx === firstMatchIdx ? firstMatchRef : undefined}
                          className={
                            isMatch
                              ? "border-b border-default-200 bg-warning-100"
                              : "border-b border-default-200 hover:bg-default-50"
                          }
                          data-highlight={isMatch ? "true" : undefined}
                        >
                          {/* Row number */}
                          <td className="px-3 py-2 text-xs text-default-500 border-r border-default-200 bg-default-50">
                            {rowIdx + 1}
                          </td>
                          {currentSheet.headers.map((_, cellIdx) => {
                            const cellValue = row[cellIdx];

                            return (
                              <td
                                key={cellIdx}
                                className="px-3 py-2 text-xs text-default-700 border-r border-default-200 whitespace-nowrap"
                              >
                                {cellValue !== null && cellValue !== undefined
                                  ? String(cellValue)
                                  : ""}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Show info if table is truncated */}
        {currentSheet.rows.length === 1000 && (
          <div className="text-xs text-default-500 text-center">
            Showing first 1,000 rows (sheet may contain more)
          </div>
        )}
      </div>
    </div>
  );
};

export default ExcelViewer;
